import React from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../../lib/api';
import {
    BlocATraiter, BlocJournee, BlocListe, BlocSemaine, BlocCoutSemaine, BlocPremierJour,
    BlocAlertes, BlocSupport, type Todo, type Service, type Ligne, type Tone,
} from './RestaurationBlocs';
import { PIPELINE_STAGE_LABELS } from '../SuiviPrivatisation/pipelineStages';

/**
 * Tableau de bord d'un établissement de Restauration (maquette 17a et 17b).
 *
 * L'écran répond à trois questions, dans cet ordre : qu'est-ce qui attend une décision, à quoi
 * ressemble la journée, et où en est la semaine. Tout vient d'un seul appel : huit blocs qui
 * interrogeraient chacun leur module rendraient la page d'accueil lente à ouvrir.
 *
 * Un bloc sans donnée ne s'affiche pas. Deux blocs de la maquette sont absents faute de source :
 * les couverts prévus par service et les acomptes de privatisation (décisions d'Emeni, 16/09).
 */

interface Donnees {
    premier_jour: boolean;
    todos: Todo[];
    services: Service[];
    absences_du_jour: { nom: string; type: string }[];
    dossiers: { id: string; name: string; stage: string; when: string | null; detail: string }[];
    a_venir: { id: string; name: string; start_time: string; detail: string }[];
    semaine: { week_start: string; publiee: boolean; creneaux: number; salaries: number; cout: number | null; creneaux_sans_taux?: number };
    jours: { date: string; label: string; midi: number; soir: number; aujourdhui: boolean }[];
}

/** L'urgence se lit sur l'étape : un devis parti sans réponse appelle l'œil, un jour J confirmé non. */
const toneEtape = (stage: string): Tone => {
    if (stage === 'devis_envoye') return 'danger';
    if (stage === 'jour_j') return 'succes';
    if (stage === 'devis_signe' || stage === 'collecte_menus') return 'attention';
    return 'info';
};

const dateCourte = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' });
};

export const RestaurationDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = React.useState<Donnees | null>(null);
    const [chargement, setChargement] = React.useState(true);

    React.useEffect(() => {
        dashboardApi.getRestaurationDashboard()
            .then((d: any) => setData(d && !d.error ? d : null))
            .catch(() => setData(null))
            .finally(() => setChargement(false));
    }, []);

    if (chargement) {
        return (
            <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(255px,1fr))]">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-[120px] rounded-xl bg-surface border border-border animate-pulse" />
                ))}
            </div>
        );
    }

    if (!data) return null;

    if (data.premier_jour) {
        return <BlocPremierJour onEtape={(href) => navigate(href)} />;
    }

    const dossiers: Ligne[] = data.dossiers.map((d) => ({
        id: d.id,
        name: d.name,
        when: dateCourte(d.when),
        detail: d.detail,
        // Le registre du module, pas une copie locale : la copie qui vivait ici nommait les étapes
        // « menus », « prestataires », « fournisseurs »... des codes qui n'existent pas. Sept étapes sur
        // douze s'affichaient donc en brut à l'écran (« collecte_menus »), vu en test le 18/09.
        step: (PIPELINE_STAGE_LABELS as Record<string, string>)[d.stage] || d.stage,
        tone: toneEtape(d.stage),
    }));

    const aVenir: Ligne[] = data.a_venir.map((e) => ({
        id: e.id,
        name: e.name,
        when: dateCourte(e.start_time),
        detail: e.detail,
        tone: 'info' as Tone,
    }));

    // Les alertes du jour : pour l'instant les absences en cours, seule source réellement détectée.
    const alertes = data.absences_du_jour.map((a) => ({
        title: 'Absence en cours',
        detail: `${a.nom} — ${a.type}.`,
        tone: 'danger' as Tone,
    }));

    return (
        <div className="flex flex-col gap-3.5">
            <BlocATraiter todos={data.todos} onOuvrir={(href) => navigate(href)} />

            <div className="grid gap-3 items-start lg:[grid-template-columns:minmax(0,1.85fr)_minmax(0,1fr)]">
                <div className="flex flex-col gap-3 min-w-0">
                    <BlocJournee services={data.services} absences={data.absences_du_jour} />
                    <BlocListe
                        titre="Privatisations à faire avancer"
                        lien="/suivi-privatisation"
                        lienLabel="Tous les dossiers"
                        lignes={dossiers}
                        onOuvrir={(id) => navigate(`/suivi-privatisation/${id}`)}
                        vide="Aucun dossier en cours."
                    />
                </div>

                <div className="flex flex-col gap-3 min-w-0">
                    <BlocListe
                        titre="Événements à venir"
                        lien="/evenementiel"
                        lienLabel="Le calendrier"
                        lignes={aVenir}
                        onOuvrir={() => navigate('/evenementiel')}
                        vide="Aucun événement programmé."
                    />
                    <BlocAlertes alertes={alertes} />
                    <BlocCoutSemaine semaine={data.semaine} onEtape={(href) => navigate(href)} />
                    <BlocSupport />
                </div>
            </div>

            <BlocSemaine jours={data.jours} onOuvrirPlanning={() => navigate('/planning')} />
        </div>
    );
};
